import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import queryString from 'query-string';
import ms from 'ms';
import lodashPick from 'lodash/fp/pick';
import lodashOmit from 'lodash/fp/omit';

import {
  SR_API_ACTION_SELF_SHOWING_LOCK_UNLOCK,
  SR_API_ACTION_GET_LISTING_GATE_CODE,
  SR_API_ACTION_LISTING_SUNLOCK_GEOLOCATION_ERROR,
  SR_API_ACTION_CHECK_SELF_TOUR_REQUEST,
  SR_API_ACTION_TOUR_CREATE_SELF_SHOWING,
  SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_INIT,
  SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_SUBMIT,
  SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
  SR_API_ACTION_USER_GENERATE_AUTO_LOGIN_URL,
  SR_API_ACTION_INSTALL_ORDERS_LOCK_TESTED,
  SR_API_ACTION_LISTING_ALARM_TURN_OFF_FAILED,
  SR_API_ACTION_MANUAL_ID_VERIFICATION_CREATE,
  SR_API_ACTION_MANUAL_ID_VERIFICATION_GET_BY_ID,
  SR_API_ACTION_SELF_SHOWING_REPORT_SCAM,
} from 'api/actionTypes';
import {
  TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING_SUNLOCK,
  TOUR_STATUS_SCHEDULED,
  TOUR_TYPE_SELF_SHOWING,
  TOUR_ADVERTISING_SCAM_SITES,
} from 'common/tourConstants';
import {
  SELF_SHOWING_LOCK_TYPE_SUNLOCK,
  SELF_SHOWING_LOCK_TYPE_CODEBOX,
} from 'common/selfShowingLockConstants';
import {
  MANUAL_ID_VERIFICATION_STATUS_PENDING,
  MANUAL_ID_VERIFICATION_STATUS_RETRY,
} from 'common/manualIdVerificationConstants';
import {
  LISTING_STATUS_ACTIVE,
  LISTING_MAX_DISTANCE_WITH_USER_IN_KM,
  LISTING_PHONE_CONTACT_SUPPORT_SUNLOCK_ISSUE,
  LISTING_PHONE_CONTACT_SUPPORT_FIELD_OPS,
} from 'common/listingConstants';
import { SR_ERROR_LISTING_NOT_AVAILABLE_TO_VISIT_OFF_HOURS } from 'common/errorCodes';
import isUserAgentMobileApp, {
  isUserAgentIphoneApp,
  isUserAgentMobile,
} from 'common/util/isUserAgentMobileApp';
import {
  getListingFullAddressString,
  isSelfShowingListingAvailableToVisit,
  makeListingAddressStreetDisplay,
  makeListingFullAddressString,
} from 'common/listingHelpers';

import {
  hasRoleAnonymous,
  hasRoleBuyerSideAgent,
  hasRoleShadowBanned,
  hasRoleSelfShowingTester,
  hasRoleFieldOps,
  USER_ROLE_ADMIN,
  USER_ROLE_FIELD_OPS,
  USER_ROLE_SELF_SHOWING_TESTER,
  hasRoleDriver,
} from 'common/userRoles';
import {
  ID_VERIFICATION_STATUS_SUCCESS,
  ID_VERIFICATION_FLOW_SELF_TOUR,
} from 'common/idVerificationConstants';
import sendDataToMobileApp, {
  SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
  SR_MOBILE_APP_ACTION_TYPE_ID_VERIFICATION,
} from 'client/helpers/sendDataToMobileApp';
import getTourListingsGoogleMapsDirectionsUrl from 'common/util/getTourListingsGoogleMapsDirectionsUrl';
import getDistanceFromLatLonInKm from 'common/util/getDistanceFromLatLonInKm';
import convertKmToFt from 'common/util/convertKmToFt';

import withApiState from 'client/helpers/withApiState';
import withLocationQuery from 'client/helpers/withLocationQuery';
import withJsScript from 'client/helpers/withJsScript';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

import withGeolocation, {
  GEOLOCATION_STATE_REQUIRE_PERMISSION,
  GEOLOCATION_STATE_ENABLE,
  GEOLOCATION_STATE_DISABLED,
  GEOLOCATION_ERROR_USER_DENIED_PERMISSION,
} from 'client/helpers/withGeolocation';
import postWithState from 'client/actions/postWithState';
import TourActions from 'client/actions/Tour';
import ListingActions from 'client/actions/Listing';
import NavigatorActions from 'client/actions/Navigator';
import IdVerificationsActions from 'client/actions/IdVerifications';
import MarketActions from 'client/actions/Market';
import branch from 'client/core/branch';

import ButtonDeprecated, {
  BUTTON_THEME_BLUE,
  BUTTON_THEME_GREEN,
  BUTTON_THEME_DARK_GRAY,
  BUTTON_THEME_BLUE_GREYCLIFF,
  BUTTON_THEME_GREY_ADOBE,
  BUTTON_THEME_RED,
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrTextList from 'client/components/SrTextList';
import SrDarkModal from 'client/components/SrDarkModal';
import SrExternalLink from 'client/components/SrExternalLink';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrSafariToolbarSpace from 'client/components/SrSafariToolbarSpace';
import SrSelfShowingVerificationModal from 'client/components/SrSelfShowingVerificationModal';
import { SR_WEB_APP_ACTION_TYPE_ID_VERIFICATION_RESULT } from 'client/components/SrMobileAppEventListener';
import SrSelfShowingLockForm from 'client/components/SrSelfShowingLockForm';
import SrSelfShowingCodeboxIssueForm from 'client/components/SrSelfShowingCodeboxIssueForm';
import SrSelfTourDoNotLockModal from 'client/components/SrSelfTourDoNotLockModal';
import SrSelfTourCodeboxReturnKeyModal from 'client/components/SrSelfTourCodeboxReturnKeyModal';
import SrSelfShowingInstallationSuccessModal from 'client/components/SrSelfShowingInstallationSuccessModal';
import SrSelfShowingTroubleshootModal from 'client/components/SrSelfShowingTroubleshootModal';
import SrPhotosCarousel from 'client/components/SrPhotosCarousel';
import SrSelfShowingAdvertisingSiteModal from 'client/components/SrSelfShowingAdvertisingSiteModal';
import SrSunlockStatus from 'client/components/SrSunlockStatus/SrSunlockStatus';

export const SELF_TOURING_STATE_INFO = 'SELF_TOURING_STATE_INFO';
export const SELF_TOURING_STATE_SHOW_STEPS = 'SELF_TOURING_STATE_SHOW_STEPS';
export const SELF_TOURING_STATE_PROPERTY_CLOSED = 'SELF_TOURING_STATE_PROPERTY_CLOSED';
export const SELF_TOURING_STATE_MOBILE_ANDROID_ID_VERIFICATION =
  'SELF_TOURING_STATE_MOBILE_ANDROID_ID_VERIFICATION'; // eslint-disable-line max-len
export const SELF_TOURING_STATE_ID_VERIFICATION = 'SELF_TOURING_STATE_ID_VERIFICATION';
export const SELF_TOURING_STATE_ID_VERIFICATION_MODAL = 'SELF_TOURING_STATE_ID_VERIFICATION_MODAL';
export const SELF_TOURING_STATE_ID_VERIFICATION_FAILED =
  'SELF_TOURING_STATE_ID_VERIFICATION_FAILED';
export const SELF_TOURING_STATE_LOCATION_REQUIRED = 'SELF_TOURING_STATE_LOCATION_REQUIRED';
export const SELF_TOURING_STATE_CAMERA_REQUIRED = 'SELF_TOURING_STATE_CAMERA_REQUIRED';
export const SELF_TOURING_STATE_RENTER_DENIED_LOCATION =
  'SELF_TOURING_STATE_RENTER_DENIED_LOCATION';
export const SELF_TOURING_STATE_CALCULATE_LOCATION = 'SELF_TOURING_STATE_CALCULATE_LOCATION';
export const SELF_TOURING_STATE_CODEBOX_LOCATION = 'SELF_TOURING_STATE_CODEBOX_LOCATION';
export const SELF_TOURING_STATE_RENTER_NOT_NEARBY = 'SELF_TOURING_STATE_RENTER_NOT_NEARBY';
export const SELF_TOURING_STATE_GATE_CODE = 'SELF_TOURING_STATE_GATE_CODE';
export const SELF_TOURING_STATE_ADVERTISING_SITE = 'SELF_TOURING_STATE_ADVERTISING_SITE';
export const SELF_TOURING_STATE_ADVERTISING_SITE_SCAM = 'SELF_TOURING_STATE_ADVERTISING_SITE_SCAM';
export const SELF_TOURING_STATE_ADVERTISING_SITE_OK = 'SELF_TOURING_STATE_ADVERTISING_SITE_OK';
export const SELF_TOURING_STATE_MANUAL_ID_VERIFICATION =
  'SELF_TOURING_STATE_MANUAL_ID_VERIFICATION';
export const SELF_TOURING_STATE_MANUAL_ID_VERIFICATION_RETRY =
  'SELF_TOURING_STATE_MANUAL_ID_VERIFICATION_RETRY'; // eslint-disable-line max-len
export const SELF_TOURING_STATE_UNLOCK_SUNLOCK = 'SELF_TOURING_STATE_UNLOCK_SUNLOCK';
export const SELF_TOURING_STATE_UNLOCKED_SUNLOCK = 'SELF_TOURING_STATE_UNLOCKED_SUNLOCK';
export const SELF_TOURING_STATE_SHOW_CODEBOX_CODES = 'SELF_TOURING_STATE_SHOW_CODEBOX_CODES';
export const SELF_TOURING_STATE_SHOW_HOME_DETAILS = 'SELF_TOURING_STATE_SHOW_HOME_DETAILS';
export const SELF_TOURING_STATE_ALARM_TURN_OFF = 'SELF_TOURING_STATE_ALARM_TURN_OFF';
export const SELF_TOURING_STATE_ALARM_TURN_ON = 'SELF_TOURING_STATE_ALARM_TURN_ON';
export const SELF_TOURING_STATE_ALARM_TURN_OFF_FAILED = 'SELF_TOURING_STATE_ALARM_TURN_OFF_FAILED';
export const SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS =
  'SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS';
export const SELF_TOURING_STATE_INSTALLATION_SUCCESS = 'SELF_TOURING_STATE_INSTALLATION_SUCCESS';
export const SELF_TOURING_STATE_INSTALLATION_FAILED = 'SELF_TOURING_STATE_INSTALLATION_FAILED';

const SELF_TOURING_STATE_TO_TITLE_MAPPING = {
  [SELF_TOURING_STATE_GATE_CODE]: 'Gate Code',
  [SELF_TOURING_STATE_CALCULATE_LOCATION]: 'Getting location',
  [SELF_TOURING_STATE_CODEBOX_LOCATION]: 'Codebox Location',
  [SELF_TOURING_STATE_UNLOCK_SUNLOCK]: 'Unlocking this home now',
  [SELF_TOURING_STATE_UNLOCKED_SUNLOCK]: 'Unlocked!',
  [SELF_TOURING_STATE_SHOW_CODEBOX_CODES]: 'Codebox Code',
  [SELF_TOURING_STATE_SHOW_HOME_DETAILS]: 'Welcome!',
  [SELF_TOURING_STATE_ALARM_TURN_OFF]: 'Turn off the alarm',
  [SELF_TOURING_STATE_ALARM_TURN_ON]: 'Welcome!',
};

function getSelfTouringTitle({ selfTouringState, listing }) {
  if (selfTouringState === SELF_TOURING_STATE_UNLOCK_SUNLOCK && listing.codeboxLockId) {
    return 'Generating Code';
  }

  if (selfTouringState === SELF_TOURING_STATE_SHOW_HOME_DETAILS && listing.hasAlarmCode) {
    return 'The Alarm may sound...';
  }

  return SELF_TOURING_STATE_TO_TITLE_MAPPING[selfTouringState];
}

const SELF_TOURING_STATE_UNLOCKING_LIST = [
  SELF_TOURING_STATE_CALCULATE_LOCATION,
  SELF_TOURING_STATE_CODEBOX_LOCATION,
  SELF_TOURING_STATE_UNLOCK_SUNLOCK,
  SELF_TOURING_STATE_UNLOCKED_SUNLOCK,
  SELF_TOURING_STATE_GATE_CODE,
  SELF_TOURING_STATE_SHOW_CODEBOX_CODES,
  SELF_TOURING_STATE_SHOW_HOME_DETAILS,
  SELF_TOURING_STATE_ALARM_TURN_OFF,
  SELF_TOURING_STATE_ALARM_TURN_ON,
];

const pickClientInformationFields = lodashPick(['clientName', 'clientPhone', 'clientEmail']);

const omitSource = lodashOmit(['source']);

const codeboxSelfShowingInstructionPhotos = [
  '/images/codebox-self-showing-instructions-step-1.jpg',
  '/images/codebox-self-showing-instructions-step-2.jpg',
  '/images/codebox-self-showing-instructions-step-3.jpg',
];

@withGeolocation
@withJsScript({
  scriptUrl: 'https://static.vouched.id/plugin/releases/latest/index.js',
})
@withApiState({
  checkSelfTourRequestApiState: {
    action: SR_API_ACTION_CHECK_SELF_TOUR_REQUEST,
  },
})
@withLocationQuery
@branch(({ match }) => ({
  tours: ['tours'],
  userAgent: ['analytics', 'userAgent'],
  marketsById: ['markets', 'byId'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  userIdVerificationsByUserId: ['idVerifications', 'byUserId'],
  sunlockHealth: ['sunlockHealth', match.params.listingId],
}))
class SrListingSelfTouringSection extends Component {
  static propTypes = {
    redirectToApartmentFlow: PropTypes.bool,
    isApartmentFlow: PropTypes.bool,
    buttonTextOverride: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    listing: PropTypes.object.isRequired,
    marketsById: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    locationQuery: PropTypes.object.isRequired,
    tours: PropTypes.array,
    userAgent: PropTypes.object,
    renterProfilesByUserId: PropTypes.object,
    userIdVerificationsByUserId: PropTypes.object,
    hideInstructions: PropTypes.bool,
    geolocationState: PropTypes.string.isRequired,
    requestGeolocationPermission: PropTypes.func.isRequired,
    getCurrentLocation: PropTypes.func.isRequired,
    onCreateSelfTour: PropTypes.func,
    checkSelfTourRequestApiState: PropTypes.object.isRequired,
  };

  static defaultProps = {
    hideInstructions: false,
  };

  constructor(props) {
    super(props);

    this._manualIdVerificationLongPollingInterval = null;

    this.state = {
      selfTouringState: SELF_TOURING_STATE_INFO,
      idVerificationCompleted: false,
      idVerificationError: null,
      hasGateChecked: false,
      gateCode: null,
      isUserNearbyToListingLocation: null,
      userGeolocation: null,
      isOpenSelfShowingLockIssueModal: false,
      isOpenSelfShowingCodeboxIssueModal: false,
      isOpenSelfShowingDoNotLockModal: false,
      isOpenSelfShowingCodeboxReturnKeyModal: false,
      isCallSupportModalOpen: false,
      accessCodeShown: false,
      userTypeConfirmed: false,
      codeboxOtpCode: null,
      codeboxLocationStepCompleted: false,
      isThankYouSupportModalOpen: false,
      sunlockLockCode: null,
      alarmCode: null,
      alarmTurnOffInstructions: null,
      alarmTurnOnInstructions: null,
      manualIdVerification: null,
      manualIdVerificationRequested: false,
      advertisingSite: null,
      advertisingSiteModalConfirmed: false,
    };
  }

  componentDidMount() {
    const {
      locationQuery,
      listing,
      history,
      currentUser,
      dispatch,
      tours,
      redirectToApartmentFlow,
      match,
    } = this.props;

    const userIdVerification = this._getUserIdVerification();

    if (!userIdVerification && !hasRoleAnonymous(currentUser.roles)) {
      dispatch(IdVerificationsActions.getByUserSession);
    }
    if (locationQuery.source === 'unlockListing') {
      if (!currentUser.phoneVerified && !currentUser.roles.includes(USER_ROLE_FIELD_OPS)) {
        history.push(
          `/listing/${listing.id}/verify-phone?${queryString.stringify({
            ...pickClientInformationFields(locationQuery),
            source: 'unlockListing',
          })}`,
        );
        return;
      }
      if (redirectToApartmentFlow) {
        history.push(`/listing/${listing.id}/self-tour`);
        return;
      }

      // NOTE(@archr): Only unlock sunlock if there is no a tour already scheduled
      const tourScheduled = (tours || []).find(
        ({ status, type }) => status === TOUR_STATUS_SCHEDULED && type === TOUR_TYPE_SELF_SHOWING,
      );

      if (!tourScheduled) {
        this._onUnlockSunlock();
        dispatch(NavigatorActions.replace, {
          to: `/listing/${listing.id}?${queryString.stringify(omitSource(locationQuery))}`,
        });
      }
    } else if (locationQuery.showSelfShowingSteps) {
      this._setUserTypeConfirmed();
      this._onToggleModalShowInstructions();
      dispatch(NavigatorActions.replace, {
        to: `/listing/${listing.id}?${queryString.stringify(omitSource(locationQuery))}`,
      });
    }

    const shouldRequestSunlockHealth = this._shouldDisplaySunlockStatus();

    if (shouldRequestSunlockHealth) {
      dispatch(ListingActions.getSunlockHealth, {
        listingId: match.params.listingId,
        forceRefresh: true,
      });

      setInterval(() => {
        dispatch(ListingActions.getSunlockHealth, {
          listingId: match.params.listingId,
          forceRefresh: true,
        });
      }, 10000);
    }

    dispatch(MarketActions.getById, {
      listingId: listing.marketId,
    });

    global.document.addEventListener('sunroomMobileAppEvent', this.onSunroomMobileAppEvent);
  }

  componentWillUnmount() {
    this._isUnmounting = true;
    global.document.removeEventListener('sunroomMobileAppEvent', this.onSunroomMobileAppEvent);
    clearInterval(this._manualIdVerificationLongPollingInterval);
  }

  _getUserIdVerification() {
    const { currentUser, userIdVerificationsByUserId } = this.props;
    if (!hasRoleAnonymous(currentUser.roles)) {
      return userIdVerificationsByUserId[currentUser.id];
    }
    return null;
  }

  _setUserTypeConfirmed = () => {
    this.setState({
      userTypeConfirmed: true,
    });
  };

  _confirmUserTypeBeforShowSteps = () => {
    const { tours, listing, history, currentUser, locationQuery } = this.props;
    const { userTypeConfirmed } = this.state;

    const isAnonymous = hasRoleAnonymous(currentUser.roles);
    const isBuyerSideAgent = hasRoleBuyerSideAgent(currentUser.roles);

    if (
      (isAnonymous || tours.length === 0 || (isBuyerSideAgent && !locationQuery.clientName)) &&
      !currentUser.roles.includes(USER_ROLE_FIELD_OPS) &&
      !userTypeConfirmed
    ) {
      history.push(`/get-started?selfTouring=true&listingId=${listing.id}`);
      return;
    }

    this._onToggleModalShowInstructions();
  };

  _onToggleModalShowInstructions = (e) => {
    if (e) {
      preventDefaultPropagation(e);
    }

    const { selfTouringState } = this.state;

    this.setState({
      codeboxLocationStepCompleted: false,
      selfTouringState:
        selfTouringState === SELF_TOURING_STATE_INFO
          ? SELF_TOURING_STATE_SHOW_STEPS
          : SELF_TOURING_STATE_INFO,
    });
  };

  _onToggleIsOpenSelfShowingLockIssueModal = ({ showContactSupportModal } = {}) => {
    this.setState({
      isOpenSelfShowingLockIssueModal: !this.state.isOpenSelfShowingLockIssueModal,
      selfTouringState: this.state.isOpenSelfShowingLockIssueModal
        ? SELF_TOURING_STATE_INFO
        : this.state.selfTouringState,
      isCallSupportModalOpen: showContactSupportModal || false,
    });
  };

  _onToggleIsOpenSelfShowingCodeboxIssueModal = () => {
    this.setState({
      isOpenSelfShowingCodeboxIssueModal: !this.state.isOpenSelfShowingCodeboxIssueModal,
      selfTouringState: this.state.isOpenSelfShowingCodeboxIssueModal
        ? SELF_TOURING_STATE_INFO
        : this.state.selfTouringState,
      isCallSupportModalOpen: false,
      isThankYouSupportModalOpen: this.state.isOpenSelfShowingCodeboxIssueModal,
    });
  };

  _onCloseSelfShowingCodeboxIssueModal = () => {
    this.setState({
      isOpenSelfShowingCodeboxIssueModal: false,
    });
  };

  _getSlackFieldsForUnlockSunlockApi = () => {
    const { listing, currentUser, renterProfilesByUserId, userAgent } = this.props;

    const { userGeolocation } = this.state;

    const renterProfile = renterProfilesByUserId[currentUser.id];

    const slackFields = [
      {
        title: 'Location',
        value: userGeolocation ? `${userGeolocation.latitude},${userGeolocation.longitude}` : '--',
      },
      {
        title: 'Distance',
        value: userGeolocation
          ? `${parseFloat(
              convertKmToFt(
                getDistanceFromLatLonInKm(
                  listing.locationLatitude,
                  listing.locationLongitude,
                  userGeolocation.latitude,
                  userGeolocation.longitude,
                ),
              ).toFixed(2),
            )} ft.`
          : '--',
      },
      {
        title: 'Browser Name',
        value: userAgent ? userAgent.browserName : '--',
      },
      {
        title: 'Government ID',
        value: renterProfile.governmentIdPhoto,
      },
    ];
    return slackFields;
  };

  _onRequestUnlockSunlock = () => {
    const { dispatch, listing } = this.props;

    dispatch(ListingActions.checkSelfTourRequest, {
      listingId: listing.id,
    }).then(() => {
      this._onUnlockSunlock();
    });
  };

  _onUnlockSunlock = () => {
    const {
      dispatch,
      redirectToApartmentFlow,
      isApartmentFlow,
      listing,
      marketsById,
      currentUser,
      history,
      geolocationState,
      userAgent,
      locationQuery,
    } = this.props;

    const {
      selfTouringState,
      hasGateChecked,
      gateCode,
      isUserNearbyToListingLocation,
      idVerificationCompleted,
      accessCodeShown,
      codeboxLocationStepCompleted,
      manualIdVerification,
      manualIdVerificationRequested,
      advertisingSite,
      advertisingSiteModalConfirmed,
    } = this.state;

    const userIdVerification = this._getUserIdVerification();
    const isSelfShowingTester = hasRoleSelfShowingTester(currentUser.roles);
    const isFieldOps = hasRoleFieldOps(currentUser.roles);
    const market = marketsById[listing.marketId] || {};

    const idVerificationDone =
      idVerificationCompleted ||
      (userIdVerification && userIdVerification.status === ID_VERIFICATION_STATUS_SUCCESS) ||
      isSelfShowingTester ||
      isFieldOps;

    const manualIdVerificationDone =
      (manualIdVerification &&
        manualIdVerification.status !== MANUAL_ID_VERIFICATION_STATUS_PENDING) ||
      isSelfShowingTester ||
      isFieldOps;

    if (!currentUser.phoneVerified) {
      history.push(
        `/listing/${listing.id}/verify-phone?${queryString.stringify({
          ...pickClientInformationFields(locationQuery),
          source: 'unlockListing',
        })}`,
      );
      return;
    }
    if (redirectToApartmentFlow) {
      history.push(`/listing/${listing.id}/self-tour`);
      return;
    }
    if (!idVerificationDone) {
      if (isUserAgentMobileApp(userAgent)) {
        if (isUserAgentIphoneApp(userAgent)) {
          this._onSelfShowingMobileIdVerification();
        } else {
          this.setState({ selfTouringState: SELF_TOURING_STATE_MOBILE_ANDROID_ID_VERIFICATION });
        }
        return;
      }
      if (selfTouringState === SELF_TOURING_STATE_SHOW_STEPS) {
        this.setState({ selfTouringState: SELF_TOURING_STATE_CAMERA_REQUIRED });
        return;
      }
      if (!isUserAgentMobile(userAgent)) {
        this.setState({ selfTouringState: SELF_TOURING_STATE_ID_VERIFICATION_MODAL });
        return;
      }
      this.setState({ selfTouringState: SELF_TOURING_STATE_ID_VERIFICATION });
      return;
    }
    if (geolocationState === GEOLOCATION_STATE_REQUIRE_PERMISSION && !isSelfShowingTester) {
      this.setState({ selfTouringState: SELF_TOURING_STATE_LOCATION_REQUIRED });
      return;
    }
    if (geolocationState === GEOLOCATION_STATE_DISABLED && !isSelfShowingTester) {
      this.setState({ selfTouringState: SELF_TOURING_STATE_RENTER_DENIED_LOCATION });
      return;
    }
    if (geolocationState === GEOLOCATION_STATE_ENABLE && !isUserNearbyToListingLocation) {
      this._calculateLocation();
      return;
    }
    if (!hasGateChecked && selfTouringState !== SELF_TOURING_STATE_GATE_CODE && !isApartmentFlow) {
      this._getGateCode();
      return;
    }
    if (
      hasGateChecked &&
      gateCode &&
      selfTouringState !== SELF_TOURING_STATE_GATE_CODE &&
      !isApartmentFlow
    ) {
      this.setState({ selfTouringState: SELF_TOURING_STATE_GATE_CODE });
      return;
    }
    if (
      market.toursHighSecurityEnabled &&
      !manualIdVerificationDone &&
      !manualIdVerificationRequested
    ) {
      this.setState({
        manualIdVerificationRequested: true,
      });
      dispatch(postWithState, {
        action: SR_API_ACTION_MANUAL_ID_VERIFICATION_CREATE,
        payload: {
          idVerificationId: userIdVerification.id,
          userId: currentUser.id,
          listingId: listing.id,
        },
      }).then((manualIdVerification) => {
        this.setState({
          manualIdVerification,
        });
        this._startManualIdverificationLongPolling();
      });
    }

    if (!advertisingSite && !(isFieldOps || isSelfShowingTester)) {
      this.setState({
        selfTouringState: SELF_TOURING_STATE_ADVERTISING_SITE,
      });
      return;
    }

    if (TOUR_ADVERTISING_SCAM_SITES.includes(advertisingSite)) {
      dispatch(postWithState, {
        action: SR_API_ACTION_SELF_SHOWING_REPORT_SCAM,
        payload: {
          listingId: listing.id,
          advertisingSite,
        },
      });
      this.setState({
        selfTouringState: SELF_TOURING_STATE_ADVERTISING_SITE_SCAM,
      });
      return;
    }

    if (!advertisingSiteModalConfirmed && !(isFieldOps || isSelfShowingTester)) {
      this.setState({
        selfTouringState: SELF_TOURING_STATE_ADVERTISING_SITE_OK,
      });
      return;
    }

    if (market.toursHighSecurityEnabled && !manualIdVerificationDone) {
      this.setState({
        selfTouringState: SELF_TOURING_STATE_MANUAL_ID_VERIFICATION,
      });
      return;
    }

    if (
      listing.codeboxLockId &&
      !codeboxLocationStepCompleted &&
      ((listing.codeboxLocationPhotos || []).length > 0 || listing.codeboxInstructions)
    ) {
      this.setState({
        selfTouringState: SELF_TOURING_STATE_CODEBOX_LOCATION,
      });
      return;
    }

    if (
      market.toursHighSecurityEnabled &&
      manualIdVerificationDone &&
      manualIdVerification?.status === MANUAL_ID_VERIFICATION_STATUS_RETRY
    ) {
      this.setState({
        selfTouringState: SELF_TOURING_STATE_MANUAL_ID_VERIFICATION_RETRY,
      });
      return;
    }

    this.setState({
      selfTouringState: SELF_TOURING_STATE_UNLOCK_SUNLOCK,
      sunlockLockCode: null,
    });

    if (listing.electronicLockCode && !hasRoleShadowBanned(currentUser.roles)) {
      this.setState({
        selfTouringState: SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS,
      });
    } else {
      const timeout = ms('10s');
      dispatch(postWithState, {
        action: SR_API_ACTION_SELF_SHOWING_LOCK_UNLOCK,
        queryId: `${listing.id}`,
        payload: {
          listingId: listing.id,
          requestedFrom: TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING_SUNLOCK,
          advertisingSite,
        },
        timeout: timeout,
        onSuccess: (result) => {
          this.setState({
            alarmCode: result.alarmCode,
            alarmTurnOffInstructions: result.alarmTurnOffInstructions,
            alarmTurnOnInstructions: result.alarmTurnOnInstructions,
          });

          if (!result.success) {
            dispatch(postWithState, {
              action: SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
              payload: {
                userId: currentUser.id,
                listingId: listing.id,
                clientName: locationQuery.clientName,
                clientPhone: locationQuery.clientPhone,
                clientEmail: locationQuery.clientEmail,
                accessCodeShown: accessCodeShown,
                issue: `[System] Failed to unlock sunlock. Sunlock Error: ${result.error || 'N/A'}`,
              },
              onSuccess: () => {
                this.setState({
                  selfTouringState: SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS,
                });
              },
            });
          } else {
            dispatch(TourActions.getList, {
              reloadTours: true,
            }).then(() => {
              if (listing.igloohomeLockId && !hasRoleShadowBanned(currentUser.roles)) {
                this.setState({
                  selfTouringState: SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS,
                });
              } else if (
                result.type === SELF_SHOWING_LOCK_TYPE_CODEBOX &&
                !hasRoleShadowBanned(currentUser.roles)
              ) {
                if (result.success && result.pin) {
                  this.setState({
                    selfTouringState: SELF_TOURING_STATE_SHOW_CODEBOX_CODES,
                    codeboxOtpCode: result.pin,
                  });
                } else {
                  // TODO: HANDLE ERROR Codebox SCENARIO
                }
              } else {
                if (result.type === SELF_SHOWING_LOCK_TYPE_SUNLOCK && result.pin) {
                  this.setState({
                    sunlockLockCode: result.pin,
                  });
                }
                this._onUnlockedSunlock();
              }
            });
          }
        },
        onError: (unlockListingError) => {
          if (
            unlockListingError.name === 'SrApiUnlockSunlockManualVerificationAuthorizationError'
          ) {
            this._onUnlockedSunlock();
          } else if (unlockListingError.message.includes('is not active')) {
            dispatch(postWithState, {
              action: SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
              payload: {
                userId: currentUser.id,
                listingId: listing.id,
                clientName: locationQuery.clientName,
                clientPhone: locationQuery.clientPhone,
                clientEmail: locationQuery.clientEmail,
                accessCodeShown: accessCodeShown,
                issue: `[System] Failed to unlock sunlock. Sunlock Error: ${
                  unlockListingError.message || 'N/A'
                }`,
              },
              onSuccess: () => {
                this.setState({
                  selfTouringState: SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS,
                });
              },
            });
          } else if (unlockListingError.message.includes(`Timeout of ${timeout}ms exceeded`)) {
            this.setState({
              isCallSupportModalOpen: true,
            });
            dispatch(postWithState, {
              action: SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
              payload: {
                userId: currentUser.id,
                listingId: listing.id,
                issue: 'Failed to get in.',
                accessCodeShown: accessCodeShown,
                clientName: locationQuery.clientName,
                clientPhone: locationQuery.clientPhone,
                clientEmail: locationQuery.clientEmail,
              },
            });
            this._onLockSunlock();
          } else if (
            unlockListingError.name === SR_ERROR_LISTING_NOT_AVAILABLE_TO_VISIT_OFF_HOURS
          ) {
            this.setState({
              selfTouringState: SELF_TOURING_STATE_PROPERTY_CLOSED,
            });
          } else {
            if (
              hasRoleFieldOps(currentUser.roles) ||
              hasRoleSelfShowingTester(currentUser.roles) ||
              hasRoleDriver(currentUser.roles)
            ) {
              this._handleLockTest({ wasSuccessful: false });
            }
            this.setState({
              selfTouringState: SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS,
            });
          }
        },
      });
    }
  };

  _selfTourOnMobileBrowser = () => {
    const { listing, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_USER_GENERATE_AUTO_LOGIN_URL,
      payload: {
        redirectTo: `/listing/${listing.id}?source=unlockListing`,
      },
      onSuccess: (autoLoginUrl) => {
        sendDataToMobileApp({
          actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
          url: autoLoginUrl,
        });

        this._onCloseModal();
      },
    });
  };

  _requestGeolocationPermission = () => {
    const { currentUser, requestGeolocationPermission } = this.props;
    this.setState({ selfTouringState: SELF_TOURING_STATE_INFO }); // Hide component while we are asking for user's permission

    requestGeolocationPermission()
      .then(() => {
        this._calculateLocation();
      })
      .catch((error) => {
        if (
          error.message === 'User has not allowed access to system location.' &&
          currentUser.roles.includes(USER_ROLE_SELF_SHOWING_TESTER)
        ) {
          this._calculateLocation();
          return;
        }
        this._createListingSunlockGeolocationError({ errorMessage: error.message });
        this._onLockSunlock();
      });
  };

  _calculateLocation = () => {
    const { getCurrentLocation, currentUser, listing } = this.props;

    if (
      currentUser.roles.includes(USER_ROLE_SELF_SHOWING_TESTER) ||
      currentUser.roles.includes(USER_ROLE_FIELD_OPS)
    ) {
      this.setState({
        isUserNearbyToListingLocation: true,
        userGeolocation: null,
      });
      this._onUnlockSunlock();
      return;
    }

    this.setState({ selfTouringState: SELF_TOURING_STATE_CALCULATE_LOCATION });

    getCurrentLocation()
      .then((userGeolocation) => {
        const distanceBetweenUserAndListingInKm = getDistanceFromLatLonInKm(
          listing.locationLatitude,
          listing.locationLongitude,
          userGeolocation.latitude,
          userGeolocation.longitude,
        );

        if (distanceBetweenUserAndListingInKm <= LISTING_MAX_DISTANCE_WITH_USER_IN_KM) {
          this.setState({
            isUserNearbyToListingLocation: true,
            userGeolocation: userGeolocation,
          });
          this._onUnlockSunlock();
        } else {
          this._createListingSunlockGeolocationError({
            errorMessage: `Not nearby: ${parseFloat(
              convertKmToFt(distanceBetweenUserAndListingInKm),
            ).toFixed(2)} ft.`,
          });
          this.setState({
            isUserNearbyToListingLocation: false,
            selfTouringState: SELF_TOURING_STATE_RENTER_NOT_NEARBY,
          });
        }
      })
      .catch((error) => {
        this._createListingSunlockGeolocationError({ errorMessage: error.message });
        if (error.message === GEOLOCATION_ERROR_USER_DENIED_PERMISSION) {
          this.setState({ selfTouringState: SELF_TOURING_STATE_RENTER_DENIED_LOCATION });
        } else {
          this._onLockSunlock();
        }
      });
  };

  _createListingSunlockGeolocationError = ({ errorMessage }) => {
    const { dispatch, listing, locationQuery } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_LISTING_SUNLOCK_GEOLOCATION_ERROR,
      payload: {
        errorMessage,
        listingId: listing.id,
        clientName: locationQuery.clientName,
        clientPhone: locationQuery.clientPhone,
        clientEmail: locationQuery.clientEmail,
      },
    });
  };

  _onLockSunlock = () => {
    this.setState({ selfTouringState: SELF_TOURING_STATE_INFO });
  };

  _onUnlockedSunlock = () => {
    const { sunlockLockCode } = this.state;
    if (sunlockLockCode) {
      this.setState({ selfTouringState: SELF_TOURING_STATE_SHOW_HOME_DETAILS });
    } else {
      this.setState({ selfTouringState: SELF_TOURING_STATE_UNLOCKED_SUNLOCK });

      setTimeout(() => {
        this.setState({ selfTouringState: SELF_TOURING_STATE_SHOW_HOME_DETAILS });
      }, 1200);
    }
  };

  _onCloseModal = () => {
    this.setState({ selfTouringState: SELF_TOURING_STATE_INFO });
  };

  _onSelfShowingVerificationSuccess = () => {
    this.setState(
      {
        idVerificationCompleted: true,
      },
      this._onUnlockSunlock,
    );
  };

  _onSelfShowingVerificationFailure = (idVerification) => {
    const idVerificationError = ((idVerification.vouchedJob || {}).errors || []).reduce(
      (memo, { message }) => `${memo} ${message}.`.trim(),
      '',
    );

    this.setState({
      idVerificationCompleted: true,
      idVerificationError: idVerificationError,
      selfTouringState: SELF_TOURING_STATE_ID_VERIFICATION_FAILED,
    });
  };

  onSunroomMobileAppEvent = (evt) => {
    const { actionType, data } = evt.detail;
    const { dispatch, listing } = this.props;

    if (actionType === SR_WEB_APP_ACTION_TYPE_ID_VERIFICATION_RESULT) {
      if (data.success) {
        dispatch(postWithState, {
          action: SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_INIT,
          payload: {
            vouchedToken: data.vouchedJob.token,
            listingId: listing.id,
          },
        }).then(() => {
          dispatch(postWithState, {
            action: SR_API_ACTION_ID_VERIFICATION_VOUCHED_VERIFICATION_FLOW_SUBMIT,
            payload: {
              vouchedToken: data.vouchedJob.token,
              idVerificationFlow: ID_VERIFICATION_FLOW_SELF_TOUR,
            },
          }).then(() => {
            this._onSelfShowingVerificationSuccess();
          });
        });
      }
    }
  };

  _onSelfShowingMobileIdVerification = () => {
    sendDataToMobileApp({
      actionType: SR_MOBILE_APP_ACTION_TYPE_ID_VERIFICATION,
    });
  };

  _onSelfShowingVerificationRetry = () => {
    this.setState({
      idVerificationCompleted: false,
      selfTouringState: SELF_TOURING_STATE_SHOW_STEPS,
    });
  };

  _getGateCode = () => {
    const { listing, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_GET_LISTING_GATE_CODE,
      queryId: `${listing.id}`,
      payload: {
        listingId: listing.id,
      },
      onSuccess: (gateCode) => {
        this.setState(
          {
            hasGateChecked: true,
            gateCode: (gateCode || '').trim(),
          },
          this._onUnlockSunlock,
        );
      },
    });
  };

  _onPropertyAccessFailed = () => {
    const { currentUser } = this.props;
    if (hasRoleShadowBanned(currentUser.roles)) {
      return this._onCloseModal();
    }
    if (
      hasRoleFieldOps(currentUser.roles) ||
      hasRoleSelfShowingTester(currentUser.roles) ||
      hasRoleDriver(currentUser.roles)
    ) {
      this._handleLockTest({ wasSuccessful: false });
      return this.setState({ selfTouringState: SELF_TOURING_STATE_INSTALLATION_FAILED });
    }
    return this._onToggleIsOpenSelfShowingLockIssueModal();
  };

  _onCodeboxAccessFailed = () => {
    const { currentUser } = this.props;
    if (
      hasRoleFieldOps(currentUser.roles) ||
      hasRoleSelfShowingTester(currentUser.roles) ||
      hasRoleDriver(currentUser.roles)
    ) {
      this._handleLockTest({ wasSuccessful: false });
      return this._onCloseModal();
    }
    return this._onToggleIsOpenSelfShowingCodeboxIssueModal();
  };

  _onAlarmTurnOff = () => {
    this.setState({
      selfTouringState: SELF_TOURING_STATE_ALARM_TURN_OFF,
    });
  };

  _onAlarmTurnOffFailed = () => {
    const { listing, currentUser, dispatch } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_LISTING_ALARM_TURN_OFF_FAILED,
      payload: {
        listingId: listing.id,
        userId: currentUser.id,
      },
    });

    this.setState({
      selfTouringState: SELF_TOURING_STATE_ALARM_TURN_OFF_FAILED,
    });
  };

  _onAlarmTurnOffSuccess = () => {
    this.setState({
      selfTouringState: SELF_TOURING_STATE_ALARM_TURN_ON,
    });
  };

  _onPropertyAccessSuccess = () => {
    const { currentUser } = this.props;
    if (
      hasRoleFieldOps(currentUser.roles) ||
      hasRoleSelfShowingTester(currentUser.roles) ||
      hasRoleDriver(currentUser.roles)
    ) {
      this._handleLockTest({ wasSuccessful: true });
      return this.setState({ selfTouringState: SELF_TOURING_STATE_INSTALLATION_SUCCESS });
    }
    this.setState({
      isOpenSelfShowingDoNotLockModal: true,
    });
    return this._createSelfTour();
  };

  _onCodeboxAccessSuccess = () => {
    const { currentUser } = this.props;
    if (
      hasRoleFieldOps(currentUser.roles) ||
      hasRoleSelfShowingTester(currentUser.roles) ||
      hasRoleDriver(currentUser.roles)
    ) {
      this._handleLockTest({ wasSuccessful: true });
      return this.setState({ selfTouringState: SELF_TOURING_STATE_INSTALLATION_SUCCESS });
    }
    this.setState({
      isOpenSelfShowingCodeboxReturnKeyModal: true,
    });
    return this._createSelfTour();
  };

  _onCodeboxLocationContinue = () => {
    this.setState(
      {
        codeboxLocationStepCompleted: true,
      },
      this._onUnlockSunlock,
    );
  };

  _createSelfTour = () => {
    const {
      listing,
      currentUser,
      locationQuery,
      renterProfilesByUserId,
      dispatch,
      onCreateSelfTour,
    } = this.props;
    const { advertisingSite } = this.state;
    const { accessCodeShown } = this.state;
    const renterProfile = renterProfilesByUserId[currentUser.id];

    if (!hasRoleShadowBanned(currentUser.roles)) {
      dispatch(postWithState, {
        action: SR_API_ACTION_TOUR_CREATE_SELF_SHOWING,
        payload: {
          listingId: listing.id,
          requestedFrom: TOUR_SHOWING_APPLICATION_SOURCE_PAGE_LISTING_SUNLOCK,
          accessCodeShown: accessCodeShown,
          slackMessageThumbnailUrl: (renterProfile && renterProfile.governmentIdPhoto) || null,
          slackFields: this._getSlackFieldsForUnlockSunlockApi(),
          advertisingSite,
          ...pickClientInformationFields(locationQuery),
        },
        onSuccess(tourId) {
          dispatch(TourActions.getList, {
            reloadTours: true,
          });

          if (onCreateSelfTour) {
            onCreateSelfTour(tourId);
          }
        },
      });
    }

    this._onCloseModal();
  };

  _handleLockTest = async ({ wasSuccessful }) => {
    const { dispatch, listing } = this.props;

    await dispatch(postWithState, {
      action: SR_API_ACTION_INSTALL_ORDERS_LOCK_TESTED,
      payload: {
        listingId: listing.id,
        successfulTest: wasSuccessful,
      },
    });
  };

  _onClickFieldOpsCallSupport = () => {
    const { currentUser } = this.props;
    if (currentUser.roles.includes(USER_ROLE_FIELD_OPS)) {
      document.location.href = `tel:+1${LISTING_PHONE_CONTACT_SUPPORT_FIELD_OPS}`;
    }
  };

  _onClickCallSupport = () => {
    document.location.href = `tel:+1${LISTING_PHONE_CONTACT_SUPPORT_SUNLOCK_ISSUE}`;
    this._onCloseCallSupportModal();
  };

  _onClickSuccessfulInstallTest = () => {
    this.setState({ selfTouringState: SELF_TOURING_STATE_INFO });
  };

  _onCloseCallSupportModal = () => {
    this.setState({
      isCallSupportModalOpen: false,
      isThankYouSupportModalOpen: false,
    });
  };

  _onOpenSelfShowingIssueModal = () => {
    this.setState({
      isOpenSelfShowingLockIssueModal: true,
    });
  };

  _onCloseSelfShowingDoNotLockModal = () => {
    this.setState({
      isOpenSelfShowingDoNotLockModal: false,
    });
  };

  _onCloseSelfShowingCodeboxReturnKeyModal = () => {
    this.setState({
      isOpenSelfShowingCodeboxReturnKeyModal: false,
    });
  };

  _shouldDisplaySunlockStatus = () => {
    const { currentUser, listing } = this.props;

    return (
      (currentUser.roles.includes(USER_ROLE_ADMIN) ||
        currentUser.roles.includes(USER_ROLE_FIELD_OPS)) &&
      !!listing.sunlockId
    );
  };

  _isTouringEnabled = () => {
    const { listing, currentUser } = this.props;
    if (
      currentUser.roles.includes(USER_ROLE_FIELD_OPS) ||
      currentUser.roles.includes(USER_ROLE_SELF_SHOWING_TESTER)
    ) {
      return true;
    }
    return listing.status === LISTING_STATUS_ACTIVE;
  };

  _onTryTouringAgain = async () => {
    this._onCloseModal();
    const elements = document.getElementsByClassName('selfTourButton');
    if (elements.length > 0) {
      elements[0].scrollIntoView({
        behavior: 'smooth',
        alignToTop: true,
      });
    }
    this._handleLockTest({ wasSuccessful: false });
  };

  _startManualIdverificationLongPolling = () => {
    clearInterval(this._manualIdVerificationLongPollingInterval);
    this._manualIdVerificationLongPollingInterval = setInterval(
      this._checkManualIdVerificationStatus.bind(this),
      4000,
    );
  };

  _checkManualIdVerificationStatus = () => {
    const { dispatch } = this.props;
    const { manualIdVerification } = this.state;

    dispatch(postWithState, {
      action: SR_API_ACTION_MANUAL_ID_VERIFICATION_GET_BY_ID,
      payload: {
        manualIdVerificationId: manualIdVerification.id,
      },
    }).then((manualIdVerification) => {
      if (
        !this._isUnmounting &&
        manualIdVerification.status !== MANUAL_ID_VERIFICATION_STATUS_PENDING
      ) {
        clearInterval(this._manualIdVerificationLongPollingInterval);
        this.setState(
          {
            manualIdVerification,
          },
          () => {
            if (this.state.selfTouringState === SELF_TOURING_STATE_MANUAL_ID_VERIFICATION) {
              this._onUnlockSunlock();
            }
          },
        );
      }
    });
  };

  _onRetryIdVerification = () => {
    const { dispatch } = this.props;

    dispatch(IdVerificationsActions.getByUserSession, { forceRefresh: true }).then(() => {
      this.setState(
        {
          manualIdVerification: null,
          idVerificationCompleted: false,
          manualIdVerificationRequested: false,
        },
        this._onUnlockSunlock,
      );
    });
  };

  render() {
    const {
      selfTouringState,
      gateCode,
      isOpenSelfShowingLockIssueModal,
      isOpenSelfShowingCodeboxIssueModal,
      isOpenSelfShowingDoNotLockModal,
      isOpenSelfShowingCodeboxReturnKeyModal,
      idVerificationError,
      isCallSupportModalOpen,
      isThankYouSupportModalOpen,
      accessCodeShown,
      codeboxOtpCode,
      sunlockLockCode,
      alarmCode,
      alarmTurnOffInstructions,
      alarmTurnOnInstructions,
      advertisingSite,
    } = this.state;
    const {
      redirectToApartmentFlow,
      isApartmentFlow,
      buttonTextOverride,
      listing,
      marketsById,
      userAgent,
      currentUser,
      hideInstructions,
      dispatch,
      checkSelfTourRequestApiState,
      locationQuery,
      sunlockHealth,
    } = this.props;

    const listingGoogleMapsDirectionsUrl = getTourListingsGoogleMapsDirectionsUrl({
      listings: [listing],
      showings: [
        {
          listingId: listing.id,
          status: listing.showingStatus,
        },
      ],
      isIphoneApp: isUserAgentIphoneApp(userAgent),
    });

    const isTouringEnabled = this._isTouringEnabled();
    const market = marketsById[listing.marketId] || {};
    const shouldDisplaySunlockStatus = this._shouldDisplaySunlockStatus();
    const sunlockStatus = sunlockHealth?.sunlockStatus || {};
    const fullListingAddress = makeListingFullAddressString(listing);
    const isAvailableToVisit = market.timezone
      ? isSelfShowingListingAvailableToVisit({ timezone: market.timezone })
      : false;

    let disableCloseModal = false;
    let getIntoThePropertyDOM = null;
    if (selfTouringState === SELF_TOURING_STATE_SHOW_HOME_DETAILS) {
      disableCloseModal = true;
      getIntoThePropertyDOM = (
        <SrBox className="getIntoProperty">
          <SrText
            font="whitney-sc"
            fontStyle="italic"
            weight="bold"
            align="center"
            size="larger"
            display="block"
          >
            did you get into the home?
          </SrText>
          <SrFlex justify="between" className="actions">
            <ButtonDeprecated theme={BUTTON_THEME_DARK_GRAY} onClick={this._onPropertyAccessFailed}>
              no
            </ButtonDeprecated>
            <ButtonDeprecated
              theme={BUTTON_THEME_GREEN}
              onClick={listing.hasAlarmCode ? this._onAlarmTurnOff : this._onPropertyAccessSuccess}
            >
              yes
            </ButtonDeprecated>
          </SrFlex>
        </SrBox>
      );
    }

    if (
      selfTouringState === SELF_TOURING_STATE_SHOW_CODEBOX_CODES ||
      selfTouringState === SELF_TOURING_STATE_ALARM_TURN_OFF ||
      selfTouringState === SELF_TOURING_STATE_ALARM_TURN_ON
    ) {
      disableCloseModal = true;
    }

    const listingPhoto = listing.sunroomPhotoUrls[0] || '/images/self-showing-lock-form.svg';
    let buttonText = isTouringEnabled ? 'visit this home' : 'not available: leased';

    if (buttonTextOverride) {
      buttonText = buttonTextOverride;
    }

    return (
      <div className="SrListingSelfTouringSection">
        <div className={classnames('listingSection', { hideInstructions })}>
          <span className="headerTitle">open house every day</span>
          <span className="headerSubtitle">
            Visit this home on your own, any day between 8am - 8pm. The door has a smart lock that
            will unlock for you once you click the button below!
          </span>
          {shouldDisplaySunlockStatus ? (
            <SrSunlockStatus
              listingAddress={fullListingAddress}
              sunlockStatus={sunlockStatus?.status}
              sunlockSerial={sunlockStatus?.serialNumber}
              sunlockLastPingDate={sunlockStatus?.lastPingDate}
              sunlockLogs={sunlockHealth?.sunlockLogsHistory || []}
              internetConnection={sunlockHealth?.internetConnection || {}}
              onFixSunlockConnectionClick={() => {
                this.setState({ selfTouringState: SELF_TOURING_STATE_INFO });
              }}
            />
          ) : (
            <div className="governmentIdContainer">
              <div className="gonvernmentIdIcon" />
              <span className="governmentIdText">
                Government ID required for identity verification
              </span>
            </div>
          )}
          <ButtonDeprecated
            className="selfTourButton"
            onClick={
              redirectToApartmentFlow || isApartmentFlow
                ? this._onUnlockSunlock
                : this._confirmUserTypeBeforShowSteps
            }
            disabled={!isTouringEnabled}
          >
            {buttonText}
          </ButtonDeprecated>
          <div className="instructions">
            <span className="text">
              Drive to this home
              <span className="dotLight">•</span>
            </span>
            <SrExternalLink className="link" link={listingGoogleMapsDirectionsUrl}>
              Get Directions
            </SrExternalLink>
          </div>
        </div>

        {selfTouringState === SELF_TOURING_STATE_SHOW_STEPS &&
          (isAvailableToVisit || currentUser.roles.includes(USER_ROLE_FIELD_OPS)) && (
            <SrDarkModal>
              <div className="SrListingSelfTouringSection stepsModal">
                <span className="headerTitle">Let's go!</span>
                <div className="x-close" onClick={this._onToggleModalShowInstructions} />
                <div className="divider" />
                <div>
                  <span className="step">step 1</span>
                  <span className="details">
                    {'Go to '}
                    <SrExternalLink className="link" link={listingGoogleMapsDirectionsUrl}>
                      {`${getListingFullAddressString(listing)}`}
                    </SrExternalLink>
                    &nbsp;between 8am&nbsp;-&nbsp;8pm any day.
                  </span>
                  <span className="step">step 2</span>
                  <span className="details">Open Sunroom & tap "unlock now."</span>
                  <span className="step">step 3</span>
                  <span className="details">
                    <span className={classnames({ alarm: listing.hasAlarmCode })}>
                      {listing.hasAlarmCode ? 'Turn off the alarm ' : 'Come on in '}
                    </span>
                    and take a look around.
                  </span>
                </div>
                <div className="butttonWrapper">
                  <ButtonDeprecated
                    onClick={this._onRequestUnlockSunlock}
                    disable={checkSelfTourRequestApiState.inProgress}
                  >
                    unlock now
                  </ButtonDeprecated>
                </div>
              </div>
            </SrDarkModal>
          )}

        {((selfTouringState === SELF_TOURING_STATE_SHOW_STEPS && !isAvailableToVisit) ||
          selfTouringState === SELF_TOURING_STATE_PROPERTY_CLOSED) &&
          !currentUser.roles.includes(USER_ROLE_FIELD_OPS) && (
            <SrDarkModal>
              <div className="SrListingSelfTouringSection lockedHome">
                <span className="headerTitle">Closed for the night</span>
                <div className="x-close" onClick={this._onCloseModal} />
                <div className="divider" />
                <div>
                  <span className="step">open hours are 8am - 8pm</span>
                  <span className="details">
                    Sorry this property is closed for the night. Please come back another day
                    between 8am - 8pm to tour this home!
                  </span>
                </div>
                <div className="butttonWrapper">
                  <ButtonDeprecated onClick={this._onCloseModal}>ok thanks</ButtonDeprecated>
                </div>
              </div>
            </SrDarkModal>
          )}

        {selfTouringState === SELF_TOURING_STATE_MOBILE_ANDROID_ID_VERIFICATION && (
          <SrSimpleMessageModal
            iconUrl="/images/verify-identity-blue.svg"
            headline="Verify Your Identity"
            underline
            message={
              <SrText size="18" lineHeight="30px">
                {'For security purposes, we require renters to go through a quick '}
                {'verification before touring a property on your own.'}
              </SrText>
            }
            multiButtons={[
              {
                buttonText: (
                  <SrFlex align="center" justify="center">
                    <SrIcon className="external-link-white" marginRight="8px" />
                    Verify Identity
                  </SrFlex>
                ),
                buttonTheme: BUTTON_THEME_BLUE_GREYCLIFF,
                onButtonClick: this._selfTourOnMobileBrowser,
              },
              {
                buttonText: 'Cancel',
                buttonTheme: BUTTON_THEME_GREY_ADOBE,
                onButtonClick: this._onCloseModal,
              },
            ]}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}

        {selfTouringState === SELF_TOURING_STATE_ID_VERIFICATION && (
          <SrSelfShowingVerificationModal
            crossCheckVerification={market.toursHighSecurityEnabled}
            userId={currentUser.id}
            listingId={listing.id}
            idVerificationFlow={ID_VERIFICATION_FLOW_SELF_TOUR}
            onSuccess={this._onSelfShowingVerificationSuccess}
            onFailure={this._onSelfShowingVerificationFailure}
            onClose={this._onToggleModalShowInstructions}
          />
        )}

        {selfTouringState === SELF_TOURING_STATE_CAMERA_REQUIRED && (
          <SrDarkModal onClose={this._onToggleModalShowInstructions}>
            <div
              className="SrListingSelfTouringSection locationRequiredModal"
              onClick={(event) => event.stopPropagation()}
            >
              <SrBox className="locationRequiredBodyModal">
                <span className="headerTitle">
                  Camera required
                  <br />
                  to identify {currentUser.name}
                </span>
                <div className="divider" />
                <SrFlex direction="col" align="center">
                  <SrText align="center" fontSize={18}>
                    For security, we must see your Government ID
                    <br />
                    and verify that you,{' '}
                    <span className="bold">{currentUser.name.toUpperCase()}</span>, are the person
                    on it.
                  </SrText>
                  <SrBox width={290} marginTop={37} marginBottom={26}>
                    <ButtonDeprecated onClick={this._onUnlockSunlock}>
                      i understand
                    </ButtonDeprecated>
                  </SrBox>
                  <SrText
                    color="grey300"
                    weight="medium"
                    decoration="underline"
                    cursor="pointer"
                    onClick={this._onToggleModalShowInstructions}
                  >
                    No thanks, I won’t unlock this home
                  </SrText>
                </SrFlex>
              </SrBox>
              <SrSafariToolbarSpace />
            </div>
          </SrDarkModal>
        )}

        {selfTouringState === SELF_TOURING_STATE_LOCATION_REQUIRED && (
          <SrDarkModal onClose={this._onToggleModalShowInstructions}>
            <div
              className="SrListingSelfTouringSection locationRequiredModal"
              onClick={(event) => event.stopPropagation()}
            >
              <SrBox className="locationRequiredBodyModal">
                <span className="headerTitle">Location required to unlock</span>
                <div className="divider" />
                <SrFlex direction="col" align="center">
                  <SrText align="center" fontSize={18}>
                    {'To guarantee the safety of you and the property, '}
                    {'we must verify you are at this property to unlock it.'}
                  </SrText>
                  <SrBox width={290} marginTop={37} marginBottom={26}>
                    <ButtonDeprecated onClick={this._requestGeolocationPermission}>
                      i understand
                    </ButtonDeprecated>
                  </SrBox>
                  <SrText
                    color="grey300"
                    weight="medium"
                    decoration="underline"
                    cursor="pointer"
                    onClick={this._onToggleModalShowInstructions}
                  >
                    No thanks, I won’t unlock this home
                  </SrText>
                </SrFlex>
              </SrBox>
              <SrSafariToolbarSpace />
            </div>
          </SrDarkModal>
        )}

        {selfTouringState === SELF_TOURING_STATE_ID_VERIFICATION_FAILED && (
          <SrDarkModal onClose={this._onToggleModalShowInstructions}>
            <div
              className="SrListingSelfTouringSection locationDisabledModal"
              onClick={(event) => event.stopPropagation()}
            >
              <SrBox>
                <span className="headerTitle">
                  {idVerificationError
                    ? `ID verification failed: ${idVerificationError}`
                    : 'ID verification failed.'}
                </span>
                <div className="divider" />
                <SrFlex direction="col">
                  <ButtonDeprecated onClick={this._onSelfShowingVerificationRetry}>
                    Retry
                  </ButtonDeprecated>
                </SrFlex>
              </SrBox>
              <SrSafariToolbarSpace />
            </div>
          </SrDarkModal>
        )}

        {selfTouringState === SELF_TOURING_STATE_RENTER_DENIED_LOCATION && (
          <SrDarkModal onClose={this._onToggleModalShowInstructions}>
            <div
              className="SrListingSelfTouringSection locationDisabledModal"
              onClick={(event) => event.stopPropagation()}
            >
              <SrBox className="locationDisabledBodyModal">
                <span className="headerTitle">Enable Location for Sunroom</span>
                <div className="divider" />
                <SrFlex direction="col">
                  <SrBox marginBottom={32}>
                    <SrTextList
                      ordered
                      fontSize={18}
                      lineHeight={1.67}
                      textList={
                        isUserAgentMobileApp(userAgent)
                          ? [
                              <SrFlex align="center">
                                <span>Go to "Settings"</span>
                                <div className="icon settings" />
                              </SrFlex>,
                              <SrFlex align="center">
                                <span>Scroll down and tap on "Sunroom"</span>
                                <div className="icon sunroom" />
                              </SrFlex>,
                              <SrFlex align="center">
                                <span>Tap on "Location"</span>
                                <div className="icon location" />
                              </SrFlex>,
                              <SrFlex align="center">
                                <span>Tap on "While Using the App"</span>
                                <div className="icon checkmark" />
                              </SrFlex>,
                              'Tap the yellow button below!',
                            ]
                          : [
                              'Copy the url of this page.',
                              'Open a new private/incognito tab inside your browser.',
                              'Paste and Go to the url you copied.',
                              'Retry visiting the home!',
                            ]
                      }
                    />
                  </SrBox>
                  {isUserAgentMobileApp(userAgent) && (
                    <ButtonDeprecated onClick={this._calculateLocation}>
                      it’s enabled, refresh
                    </ButtonDeprecated>
                  )}
                </SrFlex>
              </SrBox>
              <SrSafariToolbarSpace />
            </div>
          </SrDarkModal>
        )}

        {selfTouringState === SELF_TOURING_STATE_RENTER_NOT_NEARBY && (
          <SrDarkModal onClose={this._onToggleModalShowInstructions}>
            <div
              className="SrListingSelfTouringSection locationFailModal"
              onClick={(event) => event.stopPropagation()}
            >
              <SrBox className="locationFailModalBody">
                <span className="headerTitle">Are you near the home?</span>
                <div className="divider" />
                <SrFlex direction="col" align="center">
                  <SrText align="center" fontSize={18}>
                    {'Our system thinks you’re not near this property. '}
                    {'To unlock the door, you must be within 300ft of the property.'}
                  </SrText>
                  <SrBox width={290} marginTop={37} marginBottom={26}>
                    <ButtonDeprecated onClick={this._calculateLocation}>
                      i'm nearby, retry
                    </ButtonDeprecated>
                  </SrBox>
                  <SrText
                    color="grey300"
                    weight="medium"
                    decoration="underline"
                    cursor="pointer"
                    onClick={this._onToggleModalShowInstructions}
                  >
                    I'm not nearby, I'll get closer
                  </SrText>
                </SrFlex>
              </SrBox>
              <SrFlex
                direction="col"
                align="center"
                justify="center"
                className="locationFailModalFooter"
              >
                <SrText
                  color="grey"
                  decoration="underline"
                  cursor="pointer"
                  weight="medium"
                  onClick={this._onOpenSelfShowingIssueModal}
                >
                  Not working? Contact support
                </SrText>
                <SrSafariToolbarSpace />
              </SrFlex>
            </div>
          </SrDarkModal>
        )}

        {SELF_TOURING_STATE_UNLOCKING_LIST.includes(selfTouringState) && (
          <SrDarkModal onClose={disableCloseModal ? null : this._onToggleModalShowInstructions}>
            <div
              className={classnames(
                'SrListingSelfTouringSection sunlockStatusModal',
                selfTouringState,
                {
                  alarm: listing.hasAlarmCode,
                },
              )}
              onClick={(event) => event.stopPropagation()}
            >
              <span className="headerTitle">
                {getSelfTouringTitle({ selfTouringState, listing })}
              </span>
              <div className="divider" />
              {selfTouringState === SELF_TOURING_STATE_CODEBOX_LOCATION && (
                <div>
                  {(listing.codeboxLocationPhotos || []).length > 0 && (
                    <SrBox width="100%" height="311px">
                      <SrPhotosCarousel
                        photos={listing.codeboxLocationPhotos}
                        preloadedPhotos={listing.codeboxLocationPhotos}
                      />
                    </SrBox>
                  )}
                  {listing.codeboxInstructions && (
                    <SrBox marginTop="16px">
                      <SrText font="whitney" weight="600" fontSize="20px">
                        Instructions
                      </SrText>
                      <SrBox
                        padding="16px"
                        border="1px solid #D4D6D8"
                        borderRadius="10px"
                        margin="8px 0 16px"
                        maxHeight="100px"
                        overflowY="auto"
                      >
                        <SrText font="whitney" weight="500" fontSize="18px">
                          {listing.codeboxInstructions}
                        </SrText>
                      </SrBox>
                    </SrBox>
                  )}
                  <ButtonDeprecated
                    theme={BUTTON_THEME_GREEN}
                    onClick={this._onCodeboxLocationContinue}
                  >
                    continue
                  </ButtonDeprecated>
                </div>
              )}
              {/* eslint-disable-next-line max-len */}
              {[SELF_TOURING_STATE_UNLOCK_SUNLOCK, SELF_TOURING_STATE_CALCULATE_LOCATION].includes(
                selfTouringState,
              ) && (
                <div>
                  <div className="loading-gif clearfix" />
                  <div className="cancel">
                    <span onClick={this._onLockSunlock}>Cancel</span>
                  </div>
                </div>
              )}
              {selfTouringState === SELF_TOURING_STATE_UNLOCKED_SUNLOCK && (
                <div>
                  <div className="done" />
                </div>
              )}
              {selfTouringState === SELF_TOURING_STATE_SHOW_HOME_DETAILS && (
                <div>
                  {!listing.hasAlarmCode && (
                    <>
                      <SrText
                        display="block"
                        align="center"
                        color="green"
                        size="21"
                        weight="600"
                        paddingBottom="23px"
                      >
                        {makeListingAddressStreetDisplay(listing, { showUnitWord: true })}
                      </SrText>
                      <div>
                        {sunlockLockCode && (
                          <div className="sunlockCodeBox">
                            <span className="sunlockCode">{sunlockLockCode}</span>
                          </div>
                        )}
                        <span className="text">
                          {sunlockLockCode
                            ? 'Type the code above in the keypad on the front door.'
                            : 'Open the door and come on in.'}
                        </span>
                        {!sunlockLockCode && (
                          <span className="text">Other renters may swing by as well.</span>
                        )}
                      </div>
                    </>
                  )}
                  {listing.hasAlarmCode && (
                    <>
                      <span className="text">
                        If the door unlocked correctly, open the door and{' '}
                        <span className="alarm">
                          turn off the alarm with the instructions on the next page.
                        </span>
                      </span>
                    </>
                  )}
                  <SrBox marginTop={32}>{getIntoThePropertyDOM}</SrBox>
                </div>
              )}
              {selfTouringState === SELF_TOURING_STATE_ALARM_TURN_OFF && (
                <div className="content">
                  {alarmTurnOffInstructions && (
                    <>
                      <span className="title orange">alarm instructions</span>
                      <span className="text">{alarmTurnOffInstructions}</span>
                    </>
                  )}
                  <span className="title">alarm code</span>
                  <div className="sunlockCodeBox">
                    <span className="sunlockCode">{alarmCode}</span>
                  </div>
                  <span className="title">is the alarm off?</span>
                  <div className="getIntoProperty">
                    <SrFlex justify="between" className="actions">
                      <ButtonDeprecated
                        theme={BUTTON_THEME_RED}
                        onClick={this._onAlarmTurnOffFailed}
                      >
                        no, help!
                      </ButtonDeprecated>
                      <ButtonDeprecated
                        theme={BUTTON_THEME_GREEN}
                        onClick={this._onAlarmTurnOffSuccess}
                      >
                        yes
                      </ButtonDeprecated>
                    </SrFlex>
                  </div>
                </div>
              )}
              {selfTouringState === SELF_TOURING_STATE_ALARM_TURN_ON && (
                <div className="content">
                  <span className="text">
                    Please remember to turn the alarm back on when you leave.
                  </span>
                  <SrBox margin="20px 0 30px">
                    {alarmTurnOnInstructions && (
                      <>
                        <span className="title orange">how to turn on the alarm</span>
                        <span className="text bold">{alarmTurnOnInstructions}</span>
                      </>
                    )}
                  </SrBox>
                  <ButtonDeprecated
                    onClick={
                      listing.codeboxLockId
                        ? this._onCodeboxAccessSuccess
                        : this._onPropertyAccessSuccess
                    }
                  >
                    ok i won't forget
                  </ButtonDeprecated>
                </div>
              )}
              {selfTouringState === SELF_TOURING_STATE_SHOW_CODEBOX_CODES && (
                <div className="content">
                  <SrBox width="311px" margin="-5px auto 32px">
                    <SrFlex justify="between">
                      {(codeboxOtpCode || '').split('').map((codeDigit, index) => (
                        <SrBox
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          background="linear-gradient(180deg, #FFFFFF 0%, #CCE4FF 100%)"
                          boxShadow={
                            '0px 4px 8px rgba(183, 184, 196, 0.42), ' +
                            '0px 12px 16px rgba(181, 182, 194, 0.18)'
                          }
                          textAlign="center"
                          borderRadius="8px"
                          width="71px"
                          height="71px"
                        >
                          <SrBox position="relative" top="20px">
                            <SrText font="whitney" weight="500" fontSize="42px">
                              {codeDigit}
                            </SrText>
                          </SrBox>
                        </SrBox>
                      ))}
                    </SrFlex>
                  </SrBox>
                  <SrBox margin="0 auto" width="311px" height="311px">
                    <SrPhotosCarousel
                      photos={codeboxSelfShowingInstructionPhotos}
                      preloadedPhotos={codeboxSelfShowingInstructionPhotos}
                      disablePhotoCount
                    />
                  </SrBox>
                  <SrText
                    display="block"
                    align="center"
                    color="green"
                    size="21"
                    weight="600"
                    paddingTop="24px"
                    paddingBottom="20px"
                  >
                    {makeListingAddressStreetDisplay(listing, { showUnitWord: true })}
                  </SrText>
                  <SrBox textAlign="center" className="getIntoProperty">
                    <SrText font="whitney" weight="600" fontSize="20px">
                      Did you get into the home?
                    </SrText>
                    <SrFlex justify="between" className="actions">
                      <ButtonDeprecated
                        theme={BUTTON_THEME_DARK_GRAY}
                        onClick={this._onCodeboxAccessFailed}
                      >
                        no
                      </ButtonDeprecated>
                      <ButtonDeprecated
                        theme={BUTTON_THEME_GREEN}
                        onClick={
                          listing.hasAlarmCode ? this._onAlarmTurnOff : this._onCodeboxAccessSuccess
                        }
                      >
                        yes
                      </ButtonDeprecated>
                    </SrFlex>
                  </SrBox>
                </div>
              )}

              {selfTouringState === SELF_TOURING_STATE_GATE_CODE && (
                <div className="content">
                  <span className="text">
                    1. Use this code at the gate. Please note{' '}
                    <span className="bold">this is not the code to the unit.</span>
                  </span>
                  <span className="sunlockCodeTitle">Gate Code</span>
                  <div className="sunlockCodeBox">
                    <span className="sunlockCode">{gateCode}</span>
                  </div>
                  <span className="codeCaption">
                    <span className="color-grey">Code not working? </span>
                    <span className="notWorkingDetails" onClick={this._onClickCallSupport}>
                      Call Support
                    </span>
                  </span>
                  <div style={{ height: 34 }} />
                  <div className="dividerStep" />
                  <div style={{ height: 20 }} />
                  <span className="text">
                    {'2. '}
                    <span className="bold">
                      {'Go to '}
                      {`${
                        listing.apartmentUnit ? `Unit ${listing.apartmentUnit}` : 'the front door'
                      }`}
                    </span>
                    {' and click the button below to unlock the door.'}
                  </span>
                  <div style={{ height: 22 }} />
                  <ButtonDeprecated onClick={this._onUnlockSunlock} className="codeConfirm">
                    i'm at the unit
                  </ButtonDeprecated>
                </div>
              )}
            </div>
          </SrDarkModal>
        )}
        {selfTouringState === SELF_TOURING_STATE_ID_VERIFICATION_MODAL && (
          <SrSimpleMessageModal
            underline
            iconUrl="/images/shield-phone-blue.svg"
            headline="Use your mobile device to tour."
            message={
              <div>
                <SrBox>
                  <SrText weight="600" size="16" font="greycliff">
                    Sunroom requires renters to physically be at the property for touring. Please
                    drive to the property first, and use your mobile device to request to unlock the
                    door.
                  </SrText>
                </SrBox>
              </div>
            }
            buttonText="I Understand"
            buttonTheme={BUTTON_THEME_BLUE_GREYCLIFF}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
            onButtonClick={this._onCloseModal}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_ADVERTISING_SITE && (
          <SrSelfShowingAdvertisingSiteModal
            onSubmit={({ advertisingSite }) => {
              this.setState(
                {
                  advertisingSite,
                },
                this._onUnlockSunlock,
              );
            }}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_ADVERTISING_SITE_SCAM && (
          <SrSimpleMessageModal
            iconUrl="/images/warning-scam.svg"
            headline={`We do NOT post on ${advertisingSite}`}
            underline
            onClose={() =>
              this.setState({
                selfTouringState: SELF_TOURING_STATE_INFO,
                advertisingSite: null,
              })
            }
            message={
              <div>
                <SrBox>
                  <SrText weight="500" size="21" font="greycliff">
                    {`Scammers often duplicate other people’s listings onto ${advertisingSite}, `}
                  </SrText>
                  <SrText weight="700" size="21" font="greycliff" color="red">
                    they pretend to be the owner, and trick people into paying them.
                  </SrText>
                </SrBox>
                <SrBox marginTop="25px">
                  <SrText font="greycliff" weight="700" size="21">
                    Sunroom will contact you soon to explain further 📞☀️️
                  </SrText>
                </SrBox>
              </div>
            }
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_ADVERTISING_SITE_OK && (
          <SrSimpleMessageModal
            underline
            iconUrl="/images/green-check-inverted.svg"
            headline="Great, we do advertise on that website!"
            buttonText="Unlock Door Now"
            onButtonClick={() => {
              this.setState(
                {
                  advertisingSiteModalConfirmed: true,
                },
                this._onUnlockSunlock,
              );
            }}
            buttonTheme={BUTTON_THEME_GREEN_ADOBE}
            message={
              <div>
                <SrBox>
                  <SrText weight="500" size="21" font="greycliff">
                    Enjoy touring Sunroom’s secure rentals properties! ☀️🏡
                  </SrText>
                </SrBox>
              </div>
            }
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_MANUAL_ID_VERIFICATION && (
          <SrSimpleMessageModal
            underline
            iconUrl="/images/shield-phone-blue.svg"
            headline="We’re running an extra verification."
            message={
              <div>
                <SrBox>
                  <SrText weight="600" size="16" font="greycliff">
                    We are currently looking at your ID and selfie you just submitted. You will get
                    access to open the door once they are reviewed!
                  </SrText>
                </SrBox>
                <SrBox marginTop="25px">
                  <SrText font="greycliff" weight="800" size="21" color="blue300">
                    This extra verification typically takes 60 seconds...
                  </SrText>
                </SrBox>
              </div>
            }
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_MANUAL_ID_VERIFICATION_RETRY && (
          <SrSimpleMessageModal
            underline
            iconUrl="/images/shield-phone-blue.svg"
            headline="Try Verifying Again."
            message={
              <div>
                <SrBox>
                  <SrText weight="600" size="16" font="greycliff">
                    Identity verifications can fail if your photos are unclear or if you use an ID
                    that is different than the name on your Sunroom account.
                  </SrText>
                </SrBox>
              </div>
            }
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
            buttonText="Try Again"
            buttonTheme={BUTTON_THEME_BLUE_GREYCLIFF}
            onButtonClick={this._onRetryIdVerification}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_ALARM_TURN_OFF_FAILED && (
          <SrSimpleMessageModal
            underline
            iconUrl="/images/phone-green-calling.svg"
            headline="You will get a call ASAP from Sunroom Support!"
            message={
              'We apologize for the loud alarm and any confusion. ' +
              'Our dedicated support team is here to assist you promptly.'
            }
            buttonText="Ok thanks"
            onButtonClick={this._onCloseModal}
            buttonTheme={BUTTON_THEME_GREEN_ADOBE}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_CONTACT_SUPPORT_FOR_ACCESS && (
          <SrSimpleMessageModal
            iconUrl="/images/shield-phone-blue.svg"
            headline="For security purposes, call us to get access to the property."
            multiButtons={[
              {
                buttonText: (
                  <SrFlex align="center" justify="center">
                    <SrIcon className="phone-icon-white" marginRight="8px" />
                    Get Access
                  </SrFlex>
                ),
                buttonTheme: BUTTON_THEME_BLUE_GREYCLIFF,
                onButtonClick: this._onClickCallSupport,
              },
              {
                buttonText: 'Cancel',
                buttonTheme: BUTTON_THEME_GREY_ADOBE,
                onButtonClick: this._onCloseModal,
              },
            ]}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_INSTALLATION_SUCCESS && (
          <SrSelfShowingInstallationSuccessModal
            text={
              listing.codeboxLockId
                ? 'CodeBox is working!'
                : listing.sunlockId
                ? 'Sunlock is working!'
                : 'Successful installation!'
            }
            listingPhoto={listingPhoto}
            handleComplete={this._onClickSuccessfulInstallTest}
          />
        )}
        {selfTouringState === SELF_TOURING_STATE_INSTALLATION_FAILED && (
          <SrSelfShowingTroubleshootModal
            handleCall={this._onClickFieldOpsCallSupport}
            handleTourAgain={this._onTryTouringAgain}
          />
        )}
        {isOpenSelfShowingLockIssueModal && (
          <SrSelfShowingLockForm
            selfTouringState={selfTouringState}
            {...pickClientInformationFields(locationQuery)}
            listing={listing}
            userId={currentUser.id}
            accessCodeShown={accessCodeShown}
            dispatch={dispatch}
            onClose={this._onToggleIsOpenSelfShowingLockIssueModal}
          />
        )}
        {isOpenSelfShowingCodeboxIssueModal && (
          <SrSelfShowingCodeboxIssueForm
            {...pickClientInformationFields(locationQuery)}
            listing={listing}
            userId={currentUser.id}
            dispatch={dispatch}
            onSubmit={this._onToggleIsOpenSelfShowingCodeboxIssueModal}
            onClose={this._onCloseSelfShowingCodeboxIssueModal}
          />
        )}
        {isOpenSelfShowingDoNotLockModal && (
          <SrSelfTourDoNotLockModal onSubmit={this._onCloseSelfShowingDoNotLockModal} />
        )}
        {isOpenSelfShowingCodeboxReturnKeyModal && (
          <SrSelfTourCodeboxReturnKeyModal
            onSubmit={this._onCloseSelfShowingCodeboxReturnKeyModal}
          />
        )}
        {isCallSupportModalOpen && (
          <SrSimpleMessageModal
            className="contactSupport"
            iconUrl="/images/house-illustration2x.png"
            headline="Contact support to tour the property now."
            buttonText="&nbsp;&nbsp;&nbsp;&nbsp;call support"
            buttonTheme={BUTTON_THEME_BLUE}
            onButtonClick={this._onClickCallSupport}
            subLabelText="No thanks, I don’t want to tour"
            maxWidth={345}
            maxIconWidth={144}
            onClose={this._onCloseCallSupportModal}
            onSubLabelClick={this._onCloseCallSupportModal}
          />
        )}
        {isThankYouSupportModalOpen && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Thank you for your feedback"
            message={
              <SrText size="18" lineHeight="28px">
                {'Our team is working to resolve the issue. If you need ' +
                  'assistance now, please press the call button below.'}
              </SrText>
            }
            multiButtons={[
              {
                buttonText: (
                  <SrFlex align="center" justify="center">
                    <SrIcon className="external-link-white" marginRight="8px" />
                    Call for Assistance
                  </SrFlex>
                ),
                buttonTheme: BUTTON_THEME_BLUE_GREYCLIFF,
                onButtonClick: this._onClickCallSupport,
              },
              {
                buttonText: 'Close',
                buttonTheme: BUTTON_THEME_GREY_ADOBE,
                onButtonClick: this._onCloseCallSupportModal,
              },
            ]}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            maxWidth={375}
          />
        )}
      </div>
    );
  }
}

SrListingSelfTouringSection.displayName = 'SrListingSelfTouringSection';

export default SrListingSelfTouringSection;
